import React from 'react';
import ActivityCreatedComment from './ActivityCreatedComment';
import ActivityCreatedHistory from './ActivityCreatedHistory';
import ActivityJoinedUser from './ActivityJoinedUser';
import ActivityPublishedPost from './ActivityPublishedPost';
import ActivityRepliedComment from './ActivityRepliedComment';
import ActivityThankedPost from './ActivityThankedPost';
import ErrorBoundary from '../components/ErrorBoundary';
import InfiniteScrollWaypoint from '../components/InfiniteScrollWaypoint';
import { MentionBaseProvider } from '../editor/mentions/MentionBase';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const ACTIVITIES_ACTIVITY_FRAGMENT = gql(`
  fragment Activities_activities on Activity {
    actedAt
    actor {
      id
    }
    ... on PublishedPostActivity {
      ...ActivityPublishedPost_activity
    }
    ... on ThankedPostActivity {
      ...ActivityThankedPost_activity
    }
    ... on CreatedCommentActivity {
      ...ActivityCreatedComment_activity
    }
    ... on RepliedCommentActivity {
      ...ActivityRepliedComment_activity
    }
    ... on JoinedUserActivity {
      ...ActivityJoinedUser_activity
    }
    ... on CreatedHistoryActivity {
      ...ActivityCreatedHistory_activity
    }
  }
`);

interface ActivitiesProps {
  activities: DocumentType<typeof ACTIVITIES_ACTIVITY_FRAGMENT>[];
  fetchMore: (() => void) | null;
}

const renderSingleActivity = (
  activity: DocumentType<typeof ACTIVITIES_ACTIVITY_FRAGMENT>,
) => {
  switch (activity.__typename) {
    case 'PublishedPostActivity':
      return <ActivityPublishedPost activity={activity} />;
    case 'ThankedPostActivity':
      return <ActivityThankedPost activity={activity} />;
    case 'CreatedCommentActivity':
      return <ActivityCreatedComment activity={activity} />;
    case 'RepliedCommentActivity':
      return <ActivityRepliedComment activity={activity} />;
    case 'JoinedUserActivity':
      return <ActivityJoinedUser activity={activity} />;
    case 'CreatedHistoryActivity':
      return <ActivityCreatedHistory activity={activity} />;
    default:
      return null;
  }
};

const Activities = ({ activities, fetchMore }: ActivitiesProps) => {
  const realActivities = activities.filter(
    (activity) => activity.__typename !== 'FillerActivity',
  );

  if (realActivities.length === 0 && !fetchMore) return null;

  return (
    <div className="activities" role="feed" aria-label="Recent activities">
      <div>
        {realActivities.map((activity) => (
          <ErrorBoundary
            key={`${activity.actor?.id || ''}-${activity.actedAt || ''}`}
            name="activity"
          >
            <MentionBaseProvider containerType="activity">
              {renderSingleActivity(activity)}
            </MentionBaseProvider>
          </ErrorBoundary>
        ))}
      </div>
      <InfiniteScrollWaypoint fetchMore={fetchMore} />
    </div>
  );
};

export default Activities;
